import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Button from 'react-bootstrap/Button';
import config from "../../config";
import "./PavementContextBasic.css";
import ContextType from "./ContextType";
import stateContext from '../../data/state_context';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import FloatingLabel from 'react-bootstrap/FloatingLabel';
import Form from 'react-bootstrap/Form';
import { Modal, ToggleButton, ButtonGroup } from "react-bootstrap";
import SideNav from "../SideNav/SideNav";
import HelpIcon from "../HelpIcon/HelpIcon";
import { useStateContext } from "../../context/StateContext";
import useFormValidation from "../../hooks/useFormValidation";
import ValidationError from "../ValidationError/ValidationError";

// Function to calculate the percentile
function calculatePercentile(arr, percentile) {
    arr.sort((a, b) => a - b);
    const index = (percentile / 100) * (arr.length - 1);
    if (Math.floor(index) === index) {
      return arr[index];
    } else {
      const lowerIndex = Math.floor(index);
      const upperIndex = Math.ceil(index);
      const weight = index - lowerIndex;
      return arr[lowerIndex] * (1 - weight) + arr[upperIndex] * weight;
    }
  }

  // Function to calculate the mean
  function calculateMean(arr) {
    const sum = arr.reduce((acc, val) => acc + val, 0);
    return sum / arr.length;
  }

const PavementContextBasic = () => {
    let { id } = useParams();
    let navigate = useNavigate();
    const { selectedState, setSelectedState, isStateSelected } = useStateContext();
    const { handleBlur, getFieldState, validateAll } = useFormValidation();
    const [validationErrorShow, setValidationErrorShow] = useState(false);
    const [experimentName, setExperimentName] = useState('');
    const [data, setData] = useState(ContextType);
    const [error, setError] = useState(null);
    const [errorPopupShow, setErrorPopupShow] = useState(false);
    const [stateWarningShow, setStateWarningShow] = useState(false);
    const [unitSystem, setUnitSystem] = useState('imperial'); // 'metric' or 'imperial'

    // Conversion factors
    const conversionFactors = {
        cuyd_to_m3: 0.764555,    // 1 cu yd = 0.764555 m³
        gal_to_lit: 3.78541,      // 1 gal = 3.78541 liters
        mile_to_km: 1.60934,      // 1 mile = 1.60934 km
        ft_to_m: 0.3048           // 1 ft = 0.3048 m
    };

    const toggleUnitSystem = () => {
        setUnitSystem(prev => prev === 'metric' ? 'imperial' : 'metric');
    };

    // Convert from storage units (imperial) to display units
    const displayValue = (value, unitType) => {
        if (unitSystem === 'imperial') return value;
        switch(unitType) {
            case 'volumePrice': return value * (1 / conversionFactors.cuyd_to_m3);  // $/cuyd → $/m³
            case 'liquidPrice': return value * (1 / conversionFactors.gal_to_lit);  // $/gal → $/lit
            case 'distance': return value * conversionFactors.mile_to_km;           // mile → km
            case 'length': return value * conversionFactors.ft_to_m;                // ft → m
            default: return value;
        }
    };

    // Convert from display units back to storage units (imperial)
    const inverseDisplayValue = (value, unitType) => {
        if (unitSystem === 'imperial') return value;
        switch(unitType) {
            case 'volumePrice': return value / (1 / conversionFactors.cuyd_to_m3);
            case 'liquidPrice': return value / (1 / conversionFactors.gal_to_lit);
            case 'distance': return value / conversionFactors.mile_to_km;
            case 'length': return value / conversionFactors.ft_to_m;
            default: return value;
        }
    };

    useEffect(() => {
        axios.get(`${config.apiUrl}/api/context/${id}`)
            .then(response => {
                setData(response.data);
                setSelectedState(response.data.climate.state);
            })
            .catch(error => {
                setError(error.message);
                handleErrorPopupShow();
            });
    }, [id]);

    useEffect(() => {
        axios.get(`${config.apiUrl}/api/experiment/${id}/name`)
            .then(response => {
                setExperimentName(response.data.title);
            })
            .catch(error => {
                setError(error.message);
                handleErrorPopupShow();
            });
    }, [id]);

    const handleStateChange = (event) => {
        const state = event.target.value;
        setSelectedState(state);

        // Return early if no state selected (empty option)
        if (!state || !stateContext[state]) {
            return;
        }

        const updatedData = {
            ...data,
            climate: {
              ...data.climate,
              state: state,
              annualPrecipitationDays: stateContext[state].annualPrecipitationDays,
              precipitationThreshold: stateContext[state].precipitationThreshold,
              solarRadiation: stateContext[state].solarRadiation,
            },
            costParameters: {
              ...data.costParameters,
              state: state,
              asphaltPrice: stateContext[state].asphaltPrice,
              concretePrice: stateContext[state].concretePrice,
              dieselPrice: stateContext[state].dieselPrice,
              gasPrice: stateContext[state].gasPrice,
              annualDiscountRate: stateContext[state].annualDiscountRate,
            },
            functionAndReliability: {
              ...data.functionAndReliability,
            },
            trafficContent: {
              ...data.trafficContent,
            },
          };

          // DUPLICAED CODE
          let functionType = 1;
                if(updatedData.functionAndReliability.urbanRural === 'Rural') {
                    if (updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway') {
                        functionType = 1;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial') {
                        functionType = 2
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector') {
                        functionType = 3;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Local') {
                        functionType = 4;
                    }
                } else if (updatedData.functionAndReliability.urbanRural === 'Urban') {
                    if (updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway') {
                        functionType = 5;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial') {
                        functionType = 6;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector') {
                        functionType = 7;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Local') {
                        functionType = 8;
                    }
                }

                let state_AADT_per_lane = [];
                if(updatedData.functionAndReliability.functionalSystem === 'Interstate' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Interstate'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Expressway' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Expressway'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MajorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Arterial' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MinorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MajorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Collector' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MinorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Local' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Local'];
                } else if(updatedData.functionAndReliability.functionalSystem === 'Interstate' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Interstate'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Expressway' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Expressway'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MajorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Arterial' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MinorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MajorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Collector' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MinorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Local' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Local'];
                }

                if (updatedData.trafficContent.trafficVolume === 'High') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 75));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 95));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else if (updatedData.trafficContent.trafficVolume === 'Medium') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 25));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 75));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else if (updatedData.trafficContent.trafficVolume === 'Low') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 5));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 25));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 5));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 95));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                }

                // Update aadtAllLane based on traffic volume and amount of lanes

                let aadtAllLaneMin = updatedData.aadtPerLane.min * updatedData.trafficContent.numberOfLanes;
                let aadtAllLaneMax = updatedData.aadtPerLane.max * updatedData.trafficContent.numberOfLanes;
                updatedData.aadtAllLane = {
                    min: aadtAllLaneMin,
                    max: aadtAllLaneMax,
                    mean: Math.round(calculateMean([aadtAllLaneMin, aadtAllLaneMax]))
                };


                // Special case for functionAndReliability.reliability change
                if (updatedData.functionAndReliability.reliability === 'High') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 85, max: 90, mean: 87.5 };
                } else if (updatedData.functionAndReliability.reliability === 'Medium') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 80, max: 85, mean: 82.5 };
                } else if (updatedData.functionAndReliability.reliability === 'Low') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 70, max: 80, mean: 75 };
                } else if (updatedData.functionAndReliability.reliability === 'Unspecified') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 70, max: 90, mean: 80 };
                }


                // Special case for traffic speed change
                if (updatedData.trafficContent.trafficSpeed === 'High') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 65, max: 80, mean: Math.round(calculateMean([65, 80])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 55, max: 70, mean: Math.round(calculateMean([55, 70])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 55, max: 70, mean: Math.round(calculateMean([55, 70])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Medium') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 55, max: 65, mean: Math.round(calculateMean([55, 65])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 25, max: 35, mean: Math.round(calculateMean([25, 35])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Low') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 15, max: 25, mean: Math.round(calculateMean([15, 25])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Unspecified') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 45, max: 80, mean: Math.round(calculateMean([45, 80])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 35, max: 70, mean: Math.round(calculateMean([35, 70])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 35, max: 70, mean: Math.round(calculateMean([35, 70])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 15, max: 45, mean: Math.round(calculateMean([15, 45])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                }

                // Update traffic growth
                if(updatedData.trafficContent.trafficGrowth === 'High') {
                    updatedData.trafficGrowth = { min: 2, max: 3, mean: 2.5 };
                } else if(updatedData.trafficContent.trafficGrowth === 'Medium') {
                    updatedData.trafficGrowth = { min: 1, max: 2, mean: 1.5 };
                } else if(updatedData.trafficContent.trafficGrowth === 'Low') {
                    updatedData.trafficGrowth = { min: 0.5, max: 1, mean: 0.75 };
                } else {
                    updatedData.trafficGrowth = { min: 0.5, max: 3, mean: 1.5 };
                }

                // Update truck percentage
                let truckPercentage = stateContext[updatedData.climate.state]['truck_percentage'][functionType];
                if (updatedData.trafficContent.truckPercentage === 'High') {
                    let min = Math.round(truckPercentage * 1.2);
                    let max = Math.round(truckPercentage * 1.6);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else if (updatedData.trafficContent.truckPercentage === 'Medium') {
                    let min = Math.round(truckPercentage * 0.7);
                    let max = Math.round(truckPercentage * 1.2);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else if (updatedData.trafficContent.truckPercentage === 'Low') {
                    let min = Math.round(truckPercentage * 0.2);
                    let max = Math.round(truckPercentage * 0.7);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else {
                    let min = Math.round(truckPercentage * 0.2);
                    let max = Math.round(truckPercentage * 1.6);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                }

                // update aadttPerLane
                let aadttPerLaneMin = updatedData.aadtPerLane.min * updatedData.truckPercentage.min / 100;
                let aadttPerLaneMax = updatedData.aadtPerLane.max * updatedData.truckPercentage.max / 100;
                updatedData.aadttPerLane = {
                    min: aadttPerLaneMin,
                    max: aadttPerLaneMax,
                    mean: Math.round(calculateMean([aadttPerLaneMin, aadttPerLaneMax]))
                };

                // update aadttAllLane
                let aadttAllLaneMin = aadttPerLaneMin * updatedData.trafficContent.numberOfLanes;
                let aadttAllLaneMax = aadttPerLaneMax * updatedData.trafficContent.numberOfLanes;
                updatedData.aadttAllLane = {
                    min: aadttAllLaneMin,
                    max: aadttAllLaneMax,
                    mean: Math.round(calculateMean([aadttAllLaneMin, aadttAllLaneMax]))
                };
          // END OF DUPLICATED CODE

        setData(updatedData);
    };

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        const unitType = e.target.dataset?.unitType; // Get unit type from data attribute
        const nameParts = name.split('.');  // Split the name by dots to handle nested keys, e.g., 'user.name'

        // Convert from display units to storage units (imperial) if needed
        let storageValue = value;
        if (unitType && type === 'number' && value !== '') {
            storageValue = inverseDisplayValue(parseFloat(value), unitType);
        }

        setData(prevFormData => {
            // Function to recursively update nested objects
            const updateNestedObject = (obj, parts, val) => {
                const part = parts.shift();  // Get the next property name in the path
                if (parts.length > 0) {
                    obj[part] = updateNestedObject(obj[part] || {}, parts, val);
                } else {
                    obj[part] = type === 'checkbox' ? checked : val;  // Set the value for the last part of the path
                }
                return { ...obj };
            };

            const updatedData = updateNestedObject({ ...prevFormData }, nameParts, type === 'checkbox' ? checked : storageValue);

            if (name === 'functionAndReliability.reliability' ||
                name === 'functionAndReliability.functionalSystem' ||
                name === 'functionAndReliability.urbanRural' ||
                name === 'trafficContent.trafficVolume' ||
                name === 'trafficContent.trafficSpeed' ||
                name === 'trafficContent.numberOfLanes' ||
                name === 'trafficContent.trafficGrowth' ||
                name === 'trafficContent.truckPercentage'
            ) {

                let functionType = 1;
                if(updatedData.functionAndReliability.urbanRural === 'Rural') {
                    if (updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway') {
                        functionType = 1;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial') {
                        functionType = 2
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector') {
                        functionType = 3;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Local') {
                        functionType = 4;
                    }
                } else if (updatedData.functionAndReliability.urbanRural === 'Urban') {
                    if (updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway') {
                        functionType = 5;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial') {
                        functionType = 6;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector') {
                        functionType = 7;
                    } else if (updatedData.functionAndReliability.functionalSystem === 'Local') {
                        functionType = 8;
                    }
                }

                let state_AADT_per_lane = [];
                if(updatedData.functionAndReliability.functionalSystem === 'Interstate' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Interstate'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Expressway' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Expressway'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MajorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Arterial' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MinorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MajorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Collector' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_MinorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Local' && updatedData.functionAndReliability.urbanRural === 'Rural') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Rural_Local'];
                } else if(updatedData.functionAndReliability.functionalSystem === 'Interstate' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Interstate'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Expressway' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Expressway'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Arterial' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MajorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Arterial' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MinorArterial'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Major Collector' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MajorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Minor Collector' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_MinorCollector'];
                } else if (updatedData.functionAndReliability.functionalSystem === 'Local' && updatedData.functionAndReliability.urbanRural === 'Urban') {
                    state_AADT_per_lane = stateContext[updatedData.climate.state]['FHWA_Urban_Local'];
                }

                if (updatedData.trafficContent.trafficVolume === 'High') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 75));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 95));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else if (updatedData.trafficContent.trafficVolume === 'Medium') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 25));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 75));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else if (updatedData.trafficContent.trafficVolume === 'Low') {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 5));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 25));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                } else {
                    let min = Math.round(calculatePercentile(state_AADT_per_lane, 5));
                    let max = Math.round(calculatePercentile(state_AADT_per_lane, 95));
                    updatedData.aadtPerLane = {
                        min: min,
                        max: max,
                        mean: Math.round(calculateMean([min, max]))
                    };
                }

                // Update aadtAllLane based on traffic volume and amount of lanes

                let aadtAllLaneMin = updatedData.aadtPerLane.min * updatedData.trafficContent.numberOfLanes;
                let aadtAllLaneMax = updatedData.aadtPerLane.max * updatedData.trafficContent.numberOfLanes;
                updatedData.aadtAllLane = {
                    min: aadtAllLaneMin,
                    max: aadtAllLaneMax,
                    mean: Math.round(calculateMean([aadtAllLaneMin, aadtAllLaneMax]))
                };


                // Special case for functionAndReliability.reliability change
                if (updatedData.functionAndReliability.reliability === 'High') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 85, max: 90, mean: 87.5 };
                } else if (updatedData.functionAndReliability.reliability === 'Medium') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 80, max: 85, mean: 82.5 };
                } else if (updatedData.functionAndReliability.reliability === 'Low') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 70, max: 80, mean: 75 };
                } else if (updatedData.functionAndReliability.reliability === 'Unspecified') {
                    updatedData.reliability = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 90, max: 95, mean: 92.5 }
                        : { min: 70, max: 90, mean: 80 };
                }


                // Special case for traffic speed change
                if (updatedData.trafficContent.trafficSpeed === 'High') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 65, max: 80, mean: Math.round(calculateMean([65, 80])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 55, max: 70, mean: Math.round(calculateMean([55, 70])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 55, max: 70, mean: Math.round(calculateMean([55, 70])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Medium') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 55, max: 65, mean: Math.round(calculateMean([55, 65])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 25, max: 35, mean: Math.round(calculateMean([25, 35])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Low') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 45, max: 55, mean: Math.round(calculateMean([45, 55])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 35, max: 45, mean: Math.round(calculateMean([35, 45])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 15, max: 25, mean: Math.round(calculateMean([15, 25])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                } else if (updatedData.trafficContent.trafficSpeed === 'Unspecified') {
                    updatedData.trafficSpeed = updatedData.functionAndReliability.functionalSystem === 'Interstate' || updatedData.functionAndReliability.functionalSystem === 'Expressway'
                        ? { min: 45, max: 80, mean: Math.round(calculateMean([45, 80])) }
                        : updatedData.functionAndReliability.functionalSystem === 'Major Arterial' || updatedData.functionAndReliability.functionalSystem === 'Minor Arterial'
                            ? { min: 35, max: 70, mean: Math.round(calculateMean([35, 70])) }
                            : updatedData.functionAndReliability.functionalSystem === 'Major Collector' || updatedData.functionAndReliability.functionalSystem === 'Minor Collector'
                                ? { min: 35, max: 70, mean: Math.round(calculateMean([35, 70])) }
                                : updatedData.functionAndReliability.functionalSystem === 'Local'
                                    ? { min: 15, max: 45, mean: Math.round(calculateMean([15, 45])) }
                                    : { min: 15, max: 80, mean: Math.round(calculateMean([15, 80])) };
                }

                // Update traffic growth
                if(updatedData.trafficContent.trafficGrowth === 'High') {
                    updatedData.trafficGrowth = { min: 2, max: 3, mean: 2.5 };
                } else if(updatedData.trafficContent.trafficGrowth === 'Medium') {
                    updatedData.trafficGrowth = { min: 1, max: 2, mean: 1.5 };
                } else if(updatedData.trafficContent.trafficGrowth === 'Low') {
                    updatedData.trafficGrowth = { min: 0.5, max: 1, mean: 0.75 };
                } else {
                    updatedData.trafficGrowth = { min: 0.5, max: 3, mean: 1.5 };
                }

                // Update truck percentage
                let truckPercentage = stateContext[updatedData.climate.state]['truck_percentage'][functionType];
                if (updatedData.trafficContent.truckPercentage === 'High') {
                    let min = Math.round(truckPercentage * 1.2);
                    let max = Math.round(truckPercentage * 1.6);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else if (updatedData.trafficContent.truckPercentage === 'Medium') {
                    let min = Math.round(truckPercentage * 0.7);
                    let max = Math.round(truckPercentage * 1.2);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else if (updatedData.trafficContent.truckPercentage === 'Low') {
                    let min = Math.round(truckPercentage * 0.2);
                    let max = Math.round(truckPercentage * 0.7);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                } else {
                    let min = Math.round(truckPercentage * 0.2);
                    let max = Math.round(truckPercentage * 1.6);
                    let mean = Math.round(calculateMean([min, max]))
                    updatedData.truckPercentage = { min: min, max: max, mean: mean };
                }

                // update aadttPerLane
                let aadttPerLaneMin = updatedData.aadtPerLane.min * updatedData.truckPercentage.min / 100;
                let aadttPerLaneMax = updatedData.aadtPerLane.max * updatedData.truckPercentage.max / 100;
                updatedData.aadttPerLane = {
                    min: aadttPerLaneMin,
                    max: aadttPerLaneMax,
                    mean: Math.round(calculateMean([aadttPerLaneMin, aadttPerLaneMax]))
                };

                // update aadttAllLane
                let aadttAllLaneMin = aadttPerLaneMin * updatedData.trafficContent.numberOfLanes;
                let aadttAllLaneMax = aadttPerLaneMax * updatedData.trafficContent.numberOfLanes;
                updatedData.aadttAllLane = {
                    min: aadttAllLaneMin,
                    max: aadttAllLaneMax,
                    mean: Math.round(calculateMean([aadttAllLaneMin, aadttAllLaneMax]))
                };
            }

            return updatedData;
        });
    }


    const goToPavementDesign = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.post(`${config.apiUrl}/api/experiment/${id}/rename`, { "title": experimentName })
            .then(response => { })
            .catch(error => { });

        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            // TODO: Check response correctness

            // TODO: Make sure pavement context custom object is created before navigating to the next page
            navigate(`/pavement-design/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToCustomInput = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.post(`${config.apiUrl}/api/experiment/${id}/rename`, { "title": experimentName })
            .then(response => { })
            .catch(error => { });
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/pavement-context-custom/${id}?state=${selectedState}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPedegreeMatrix = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/pedegree-matrix/${id}?backref=pavement-context-basic`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToAsphaltEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.post(`${config.apiUrl}/api/experiment/${id}/rename`, { "title": experimentName })
            .then(response => { })
            .catch(error => { });
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/asphalt-embodied-emission/${id}?backref=pavement-context-basic`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToConcreteEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.post(`${config.apiUrl}/api/experiment/${id}/rename`, { "title": experimentName })
            .then(response => { })
            .catch(error => { });
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/concrete-embodied-emission/${id}?backref=pavement-context-basic`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToResults = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/report/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const handleErrorPopupClose = () => setErrorPopupShow(false);
    const handleErrorPopupShow = () => setErrorPopupShow(true);

    // Validate all form fields before submission
    const validateForm = () => {
        const fields = [
            { name: 'climate.annualPrecipitationDays', value: data.climate.annualPrecipitationDays, type: 'number' },
            { name: 'climate.precipitationThreshold', value: data.climate.precipitationThreshold, type: 'number' },
            { name: 'climate.solarRadiation', value: data.climate.solarRadiation, type: 'number' },
            { name: 'costParameters.asphaltPrice', value: data.costParameters.asphaltPrice, type: 'number' },
            { name: 'costParameters.concretePrice', value: data.costParameters.concretePrice, type: 'number' },
            { name: 'costParameters.dieselPrice', value: data.costParameters.dieselPrice, type: 'number' },
            { name: 'costParameters.gasPrice', value: data.costParameters.gasPrice, type: 'number' },
            { name: 'costParameters.annualDiscountRate', value: data.costParameters.annualDiscountRate, type: 'number' },
            { name: 'trafficContent.pavementLength', value: data.trafficContent.pavementLength, type: 'number' },
        ];
        return validateAll(fields);
    };

    return (
        <>
            <SideNav active="pavement-context-basic" id={id} passed={[]}
                goToBasicContext={() => { }}
                goToDetailedContext={goToCustomInput}
                goToAsphaltEmbodiedEmission={goToAsphaltEmbodiedEmission}
                goToConcreteEmbodiedEmission={goToConcreteEmbodiedEmission}
                goToPedegreeMatrix={goToPedegreeMatrix}
                goToPavementDesign={goToPavementDesign}
                goToResults={goToResults}
                isStateSelected={isStateSelected}
                onNavigationBlocked={() => setStateWarningShow(true)}
            />
            <Container>
            <Row>
                <Col>
                    <FloatingLabel
                        controlId="floatingInput"
                        label="Experiment name"
                        className="mb-3" >
                        <Form.Control type="text" value={experimentName} onChange={(e) => setExperimentName(e.target.value)} />
                    </FloatingLabel>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__sub">
                        <div className="pcb__box" style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "20px" }}>
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Unit Toggle</div>
                                <ButtonGroup size="sm">
                                    <ToggleButton
                                        id="unit-toggle-metric-basic"
                                        type="checkbox"
                                        variant={unitSystem === 'metric' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'metric'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Metric
                                    </ToggleButton>
                                    <ToggleButton
                                        id="unit-toggle-imperial-basic"
                                        type="checkbox"
                                        variant={unitSystem === 'imperial' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'imperial'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Imperial
                                    </ToggleButton>
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>

                    {/* <!-- Climate --> */}
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">Climate</h3>
                        <div className="pcb__box">
                            {/* <!-- State --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">State</div>
                                <div>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: "200px" }}
                                        value={selectedState}
                                        onChange={handleStateChange}>
                                        {!isStateSelected && <option value="">Select a State</option>}
                                        {Object.keys(stateContext).map(state => (
                                            <option key={state} value={state}>{state}</option>
                                        ))}
                                    </Form.Select>
                                </div>
                            </div>

                            {/* <!-- Annual Precipitation Days --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Annual Precipitation Days
                                    <HelpIcon text="Average number of days per year with precipitation ≥0.01 inch. Affects pavement deterioration, maintenance scheduling, and Radiative forcing." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="climate.annualPrecipitationDays"
                                        id="annualPrecipitationDays"
                                        value={data.climate.annualPrecipitationDays}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                        isInvalid={getFieldState('climate.annualPrecipitationDays').isInvalid} />
                                    <ValidationError message={getFieldState('climate.annualPrecipitationDays').errorMessage} />
                                </div>
                            </div>

                            {/* <!-- Precipitation Threshold (in./hr) --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Precipitation Threshold (in./hr)
                                    <HelpIcon text="Minimum rainfall intensity (in./hr) that triggers surface runoff. Used in drainage and hydroplaning calculations." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="climate.precipitationThreshold"
                                        id="precipitationThreshold"
                                        value={data.climate.precipitationThreshold}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                        isInvalid={getFieldState('climate.precipitationThreshold').isInvalid} />
                                    <ValidationError message={getFieldState('climate.precipitationThreshold').errorMessage} />
                                </div>
                            </div>

                            {/* <!-- Solar Radiation --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Solar Radiation
                                    <HelpIcon text="Average daily solar radiation (kWh/m²/day). Influences pavement temperature, aging, and albedo effects." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="climate.solarRadiation"
                                        id="solarRadiation"
                                        value={data.climate.solarRadiation}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                        isInvalid={getFieldState('climate.solarRadiation').isInvalid} />
                                    <ValidationError message={getFieldState('climate.solarRadiation').errorMessage} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <!-- Cost Parameters --> */}
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">Cost Parameters</h3>
                        <div className="pcb__box">

                            {/* <!-- Asphalt Price --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Asphalt Price ({unitSystem === 'imperial' ? '$/cu yd' : '$/m³'})
                                    <HelpIcon text="Cost per cubic yard of hot mix asphalt (HMA) in the selected state. Used for initial construction and M&R cost estimates." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "200px" }}
                                        name="costParameters.asphaltPrice"
                                        id="asphaltPrice"
                                        data-unit-type="volumePrice"
                                        value={data.costParameters.asphaltPrice === '' ? '' : parseFloat(displayValue(Number(data.costParameters.asphaltPrice ?? 0), 'volumePrice').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, data.costParameters.asphaltPrice, 'number')}
                                        isInvalid={getFieldState('costParameters.asphaltPrice').isInvalid} />
                                    <ValidationError message={getFieldState('costParameters.asphaltPrice').errorMessage} />
                                </div>
                            </div>
                            {/* <!-- Concrete Price --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Concrete Price ({unitSystem === 'imperial' ? '$/cu yd' : '$/m³'})
                                    <HelpIcon text="Average cost per cubic yard of ready-mix concrete in the selected state" />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "200px" }}
                                        name="costParameters.concretePrice"
                                        id="concretePrice"
                                        data-unit-type="volumePrice"
                                        value={data.costParameters.concretePrice === '' ? '' : parseFloat(displayValue(Number(data.costParameters.concretePrice ?? 0), 'volumePrice').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, data.costParameters.concretePrice, 'number')}
                                        isInvalid={getFieldState('costParameters.concretePrice').isInvalid} />
                                    <ValidationError message={getFieldState('costParameters.concretePrice').errorMessage} />
                                </div>
                            </div>

                            {/* <!-- Diesel Price --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Diesel Price ({unitSystem === 'imperial' ? '$/gal' : '$/liter'})
                                    <HelpIcon text="Current diesel fuel price per gallon. Varied by state. Used to calculate vehicle operating costs and construction equipment costs." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "200px" }}
                                        name="costParameters.dieselPrice"
                                        id="dieselPrice"
                                        data-unit-type="liquidPrice"
                                        value={data.costParameters.dieselPrice === '' ? '' : parseFloat(displayValue(Number(data.costParameters.dieselPrice ?? 0), 'liquidPrice').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, data.costParameters.dieselPrice, 'number')}
                                        isInvalid={getFieldState('costParameters.dieselPrice').isInvalid} />
                                    <ValidationError message={getFieldState('costParameters.dieselPrice').errorMessage} />
                                </div>
                            </div>

                            {/* <!-- Gas Price --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Gas Price ({unitSystem === 'imperial' ? '$/gal' : '$/liter'})
                                    <HelpIcon text="Current gasoline price per gallon. Varied by state. Used to calculate passenger vehicle operating costs over the analysis period." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        step="0.01"
                                        style={{ width: "200px" }}
                                        name="costParameters.gasPrice"
                                        id="gasPrice"
                                        data-unit-type="liquidPrice"
                                        value={data.costParameters.gasPrice === '' ? '' : parseFloat(displayValue(Number(data.costParameters.gasPrice ?? 0), 'liquidPrice').toFixed(2))}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, data.costParameters.gasPrice, 'number')}
                                        isInvalid={getFieldState('costParameters.gasPrice').isInvalid} />
                                    <ValidationError message={getFieldState('costParameters.gasPrice').errorMessage} />
                                </div>
                            </div>

                            {/* <!-- Annual Discount Rate --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">
                                    Annual Discount Rate (%)
                                    <HelpIcon text="Annual rate used to convert future costs to present value." />
                                </div>
                                <div>
                                    <Form.Control
                                        type="number"
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="costParameters.annualDiscountRate"
                                        id="annualDiscountRate"
                                        value={data.costParameters.annualDiscountRate}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}
                                        onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                        isInvalid={getFieldState('costParameters.annualDiscountRate').isInvalid} />
                                    <ValidationError message={getFieldState('costParameters.annualDiscountRate').errorMessage} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* <!-- Function and Reliability --> */}
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">Function and Reliability</h3>
                        <div className="pcb__box">
                            {/* <!-- Urban-Rural Class --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">Urban-Rural Class</div>
                                <div>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="functionAndReliability.urbanRural"
                                        id="urbanRural"
                                        value={data.functionAndReliability.urbanRural}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}>
                                        <option value="Urban">Urban</option>
                                        <option value="Rural">Rural</option>
                                    </Form.Select>
                                </div>
                            </div>

                            {/* <!-- Functional System --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">Functional System</div>
                                <div>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="functionAndReliability.functionalSystem"
                                        id="functionalSystem"
                                        value={data.functionAndReliability.functionalSystem}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}>
                                        <option value="Interstate">Interstate</option>
                                        <option value="Expressway">Expressway</option>
                                        <option value="Major Arterial">Major Arterial</option>
                                        <option value="Minor Arterial">Minor Arterial</option>
                                        <option value="Major Collector">Major Collector</option>
                                        <option value="Minor Collector">Minor Collector</option>
                                        <option value="Local">Local</option>
                                    </Form.Select>
                                </div>
                            </div>

                            {/* <!-- Reliability --> */}
                            <div className="pcb__element">
                                <div className="pcb__label">Reliability</div>
                                <div>
                                    <Form.Select
                                        size="sm"
                                        style={{ width: "200px" }}
                                        name="functionAndReliability.reliability"
                                        id="reliability"
                                        value={data.functionAndReliability.reliability}
                                        disabled={!isStateSelected}
                                        onChange={handleChange}>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                    </Form.Select>
                                </div>
                            </div>
                        </div>
                    </div>

                </Col>

                <Col>

                    <div className="pcb__column">

                        {/* <!-- Traffic Content --> */}
                        <div className="pcb__sub">
                            <h3 className="pcb__sub-title">Traffic Content</h3>
                            <div className="pcb__box">
                                {/* <!-- Traffic Volume --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">
                                        Traffic Volume
                                        <HelpIcon text="High: 75th-95th percentile of state AADT. Medium: 25th-75th percentile. Low: 5th-25th percentile." />
                                    </div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.trafficVolume"
                                            id="trafficVolume"
                                            value={data.trafficContent.trafficVolume}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Truck Percentage --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">
                                        Truck Percentage
                                        <HelpIcon text="Proportion of heavy vehicles in traffic stream. High/Med/Low based on state averages for the road type." />
                                    </div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.truckPercentage"
                                            id="truckPercentage"
                                            value={data.trafficContent.truckPercentage}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Traffic Direction --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Traffic Direction</div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.trafficDirection"
                                            id="trafficDirection"
                                            value={data.trafficContent.trafficDirection}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="One-way">One-way</option>
                                            <option value="Two-way">Two-way</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Traffic Speed --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">
                                        Traffic Speed
                                        <HelpIcon text="Average operating speed. Affects fuel consumption, emissions, and pavement-vehicle interaction calculations." />
                                    </div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.trafficSpeed"
                                            id="trafficSpeed"
                                            value={data.trafficContent.trafficSpeed}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Traffic Growth --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">
                                        Traffic Growth
                                        <HelpIcon text="Annual traffic growth rate (%). High: 2-3%, Medium: 1-2%, Low: 0.5-1%." />
                                    </div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.trafficGrowth"
                                            id="trafficGrowth"
                                            value={data.trafficContent.trafficGrowth}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="High">High</option>
                                            <option value="Medium">Medium</option>
                                            <option value="Low">Low</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Number of Lanes --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Number of Lanes</div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.numberOfLanes"
                                            id="numberOfLanes"
                                            value={data.trafficContent.numberOfLanes}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="2">2</option>
                                            <option value="4">4</option>
                                            <option value="6">6</option>
                                            <option value="8">8</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Pavement Length --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Pavement Length ({unitSystem === 'imperial' ? 'mile' : 'km'})</div>
                                    <div>
                                        <Form.Control
                                            type="number"
                                            size="sm"
                                            step="0.01"
                                            style={{ width: "200px" }}
                                            name="trafficContent.pavementLength"
                                            id="pavementLength"
                                            data-unit-type="distance"
                                            value={data.trafficContent.pavementLength === '' ? '' : parseFloat(displayValue(Number(data.trafficContent.pavementLength ?? 0), 'distance').toFixed(2))}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}
                                            onBlur={(e) => handleBlur(e.target.name, data.trafficContent.pavementLength, 'number')}
                                            isInvalid={getFieldState('trafficContent.pavementLength').isInvalid} />
                                        <ValidationError message={getFieldState('trafficContent.pavementLength').errorMessage} />
                                    </div>
                                </div>

                                {/* <!-- Lane Width --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Lane Width ({unitSystem === 'imperial' ? 'ft' : 'm'})</div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.laneWidth"
                                            id="laneWidth"
                                            value={data.trafficContent.laneWidth}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="12">{unitSystem === 'imperial' ? '12' : (12 * 0.3048).toFixed(2)}</option>
                                            <option value="13">{unitSystem === 'imperial' ? '13' : (13 * 0.3048).toFixed(2)}</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Shoulder Type --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Shoulder Type</div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.shoulderType"
                                            id="shoulderType"
                                            value={data.trafficContent.shoulderType}
                                            disabled={!isStateSelected}
                                            onChange={handleChange}>
                                            <option value="AC Shoulder">AC Shoulder</option>
                                            <option value="Tied PCC" selectedState>Tied PCC</option>
                                        </Form.Select>
                                    </div>
                                </div>

                                {/* <!-- Shoulder Width --> */}
                                <div className="pcb__element">
                                    <div className="pcb__label">Shoulder Width ({unitSystem === 'imperial' ? 'ft' : 'm'})</div>
                                    <div>
                                        <Form.Select
                                            size="sm"
                                            style={{ width: "200px" }}
                                            name="trafficContent.shoulderWidth"
                                            id="shoulderWidth"
                                            value={data.trafficContent.shoulderWidth}
                                            disabled={!isStateSelected}
                                            onChange={handleChange} >
                                            <option value="8">{unitSystem === 'imperial' ? '8' : (8 * 0.3048).toFixed(2)}</option>
                                            <option value="9">{unitSystem === 'imperial' ? '9' : (9 * 0.3048).toFixed(2)}</option>
                                            <option value="10">{unitSystem === 'imperial' ? '10' : (10 * 0.3048).toFixed(2)}</option>
                                            <option value="11">{unitSystem === 'imperial' ? '11' : (11 * 0.3048).toFixed(2)}</option>
                                            <option value="12">{unitSystem === 'imperial' ? '12' : (12 * 0.3048).toFixed(2)}</option>
                                        </Form.Select>
                                    </div>
                                </div>

                            </div>
                        </div>

                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__box-footer">
                        {/* <Button variant="primary" href="/dashboard">Back</Button> */}
                        {/* <Button variant="primary" onClick={goToCustomInput}>Custom Input</Button>
                        <Button variant="primary" onClick={goToPedegreeMatrix} >Pedegree Matrix</Button> */}
                    </div>
                </Col>
                <Col>
                    <div className="pcb__box-footer-right">
                        {/* <Button variant="primary" onClick={goToConcreteEmbodiedEmission}>Concrete Embodied Emission</Button>
                        <Button variant="primary" onClick={goToAsphaltEmbodiedEmission}>Asphalt Embodied Emission</Button> */}
                        <Button variant="primary" disabled={!isStateSelected} onClick={goToCustomInput}>Detailed Traffic Data</Button>

                    </div>
                </Col>
                <Col>
                    <div className="pcb__box-footer-right">
                        {/* <Button variant="primary" onClick={goToConcreteEmbodiedEmission}>Concrete Embodied Emission</Button>
                        <Button variant="primary" onClick={goToAsphaltEmbodiedEmission}>Asphalt Embodied Emission</Button> */}
                        <Button variant="primary" disabled={!isStateSelected} onClick={goToConcreteEmbodiedEmission}>Save and Next</Button>

                    </div>
                </Col>
            </Row>

            <Modal show={errorPopupShow} onHide={handleErrorPopupClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{error}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleErrorPopupClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={stateWarningShow} onHide={() => setStateWarningShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>State Required</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please select a state before continuing.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setStateWarningShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={validationErrorShow} onHide={() => setValidationErrorShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Validation Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please fix the highlighted errors before continuing. All fields are required and number values must be greater than 0.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setValidationErrorShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>

        </Container>
        </>
    );
};

export default PavementContextBasic;
